import { Suspense, lazy } from 'react'
import { Maximizable } from './Maximize'
const BidPanel = lazy(() => import('./BidPanel').then((m) => ({ default: m.BidPanel })))
const VendorNetworkPanel = lazy(() => import('./VendorNetworkPanel').then((m) => ({ default: m.VendorNetworkPanel })))
const AccuracyPanel = lazy(() => import('./AccuracyPanel').then((m) => ({ default: m.AccuracyPanel })))
const ModelsPanel = lazy(() => import('./ModelsPanel').then((m) => ({ default: m.ModelsPanel })))
const TakeoffPanel = lazy(() => import('./TakeoffPanel').then((m) => ({ default: m.TakeoffPanel })))
const OpenKitchenDiagram = lazy(() => import('./OpenKitchenDiagram').then((m) => ({ default: m.OpenKitchenDiagram })))
import {
  DesignQuestions,
  ErrorPanel,
  GanttView,
  Legend,
  PlanExecution,
  PlanGraph,
  ProjectDesignDoc,
  RunReportPanel,
  TargetRepoControl,
  TaskInspector,
} from './PlanningView'
import { usePlan } from '../plan/usePlan'
import type { IconName } from './Icon'
import type { JobStageId, PlanBoard } from '../api'

// The artifact registry — ONE source of truth for the console's launcher dock,
// the fullscreen frame, and the summaries shown on each tile. Each entry is a
// capability the operator can open; the ids match the ones the old collapsing
// Disclosure sections used, so nothing about the underlying panels changed —
// only their container. Adding an artifact is a one-line change here.

export type ArtifactId =
  | 'questions' | 'bid' | 'takeoff' | 'board' | 'run' | 'models' | 'accuracy' | 'design-doc'

// Which artifacts a plan gets depends on WHAT IT BUILDS. A pergola has no data
// model; a service has no materials takeoff. 'hybrid' is the union — a
// smart-home install needs both. See CLAUDE.md / project_type.
const isBuilder = (t?: string) => t === 'manual' || t === 'hybrid'

export interface ArtifactMeta {
  id: ArtifactId
  label: string
  icon: IconName
  /** One-line state shown under the tile ("draft · $4,188"). */
  summary: (ctx: ArtifactCtx) => string
  /** False hides the tile entirely — an artifact that cannot exist for this plan. */
  applies?: (ctx: ArtifactCtx) => boolean
  /** True when the artifact has real content yet (drives the "empty" dim). */
  ready?: (ctx: ArtifactCtx) => boolean
  /** True when this artifact is waiting on the operator — the dock highlights it. */
  attention?: (ctx: ArtifactCtx) => boolean
  /**
   * The job stages this artifact SERVES. The dock ranks by it rather than
   * hiding: a flat row of nine equal tools reads as a settings dump, but
   * collapsing it behind a "More tools" menu would put every artifact one click
   * further from a cold load — which is the depth the structural pillar
   * measures and the whole point of the launcher. Ranking gets the calm default
   * without paying that price.
   */
  stages?: JobStageId[]
}

export interface ArtifactCtx {
  board: PlanBoard
  readiness: ReturnType<typeof usePlan>['readiness']
}

const money = (n: number) => `$${Math.round(n).toLocaleString()}`

export const ARTIFACTS: ArtifactMeta[] = [
  {
    id: 'questions',
    stages: ['scoping'],
    label: 'Questions',
    icon: 'questions',
    summary: ({ board }) => {
      const qs = board.questions ?? []
      if (qs.length === 0) return 'none surfaced'
      const openCritical = qs.filter((q) => q.critical && !(q.answer ?? '').trim()).length
      return openCritical > 0 ? `${openCritical} critical open` : 'all critical answered'
    },
    ready: ({ board }) => (board.questions ?? []).length > 0,
    attention: ({ board }) =>
      (board.questions ?? []).some((q) => q.critical && !(q.answer ?? '').trim()),
  },
  {
    id: 'bid',
    stages: ['pricing', 'proposed'],
    label: 'Estimate',
    icon: 'estimate',
    applies: ({ board }) => isBuilder(board.project_type),
    summary: ({ readiness }) =>
      readiness?.bid_status ? `${readiness.bid_status} · ${money(readiness.bid_total ?? 0)}` : 'not generated',
    ready: ({ readiness }) => !!readiness?.bid_status,
  },
  {
    id: 'takeoff',
    stages: ['pricing'],
    label: 'Takeoff',
    icon: 'takeoff',
    applies: ({ board }) => isBuilder(board.project_type),
    summary: ({ readiness }) => (readiness?.bid_status ? 'what to buy' : 'needs an estimate'),
    ready: ({ readiness }) => !!readiness?.bid_status,
  },
  {
    id: 'board',
    stages: ['concept', 'scoping', 'building'],
    label: 'Task board',
    icon: 'board',
    summary: ({ board }) => `${board.tasks.length} task${board.tasks.length === 1 ? '' : 's'}`,
    ready: ({ board }) => board.tasks.length > 0,
  },
  {
    id: 'run',
    stages: ['building'],
    label: 'Finalize & run',
    icon: 'run',
    summary: ({ board }) => (board.status === 'finalized' ? 'finalized' : 'draft'),
    ready: () => true,
  },
  {
    id: 'design-doc',
    stages: ['concept', 'scoping'],
    label: 'Design doc',
    icon: 'doc',
    summary: ({ board }) => (board.design_doc ? 'written' : 'not written'),
    ready: ({ board }) => !!board.design_doc,
  },
  {
    id: 'models',
    stages: ['scoping', 'pricing'],
    label: 'Model picks',
    icon: 'models',
    // NOT tech-only: the roles this tunes (decompose, materials refiner, chat
    // intent, rate estimates) run on builder plans too — two of them exist
    // *for* builder plans. Gating it made model tuning unreachable from a
    // manual plan (caught by the structural pillar's capability probe).
    summary: () => 'which model runs each step',
    ready: () => true,
  },
  {
    id: 'accuracy',
    stages: ['closing'],
    label: 'Estimate accuracy',
    icon: 'accuracy',
    applies: ({ board }) => isBuilder(board.project_type),
    summary: () => 'estimate vs. actual',
    // Accuracy compares a PROPOSAL against what the job actually cost, so
    // without a proposal there is nothing to compare and the panel says exactly
    // that: "no proposal recorded for this plan yet". Claiming ready:true made
    // it render an all-but-blank card on a surface meant for finished work.
    ready: ({ readiness }) => !!readiness?.bid_status && readiness.bid_status !== 'draft',
  },
]

/** The artifacts that apply to this plan, in dock order. */
export function visibleArtifacts(ctx: ArtifactCtx): ArtifactMeta[] {
  return ARTIFACTS.filter((a) => (a.applies ? a.applies(ctx) : true))
}

export function artifactMeta(id: ArtifactId): ArtifactMeta | undefined {
  return ARTIFACTS.find((a) => a.id === id)
}

// PlanArtifact renders one artifact's real panel. It pulls everything from the
// plan context (the same values the old PlanningView destructured), so the dock
// and the fullscreen frame can render any artifact without prop-threading.
export function PlanArtifact({ id }: { id: ArtifactId }) {
  return (
    // The heavy panels arrive on demand — opening an artifact is always an
    // explicit click, so nothing here needs to be parsed on a cold load.
    <Suspense fallback={<p className="state">Loading…</p>}>
      <PlanArtifactBody id={id} />
    </Suspense>
  )
}

function PlanArtifactBody({ id }: { id: ArtifactId }) {
  const p = usePlan()
  const board = p.snap?.board
  if (!board) return null
  // The task the board views have selected — the inspector's subject.
  const selectedTask = p.selected ? board.tasks.find((t) => t.id === p.selected) ?? null : null

  switch (id) {
    case 'questions':
      return <DesignQuestions questions={board.questions ?? []} onAnswer={p.answerQuestion} />

    case 'bid':
      return (
        <>
          <BidPanel board={board} />
          {board.mode === 'corporate' ? <VendorNetworkPanel board={board} /> : null}
        </>
      )

    case 'takeoff':
      return <TakeoffPanel board={board} />

    case 'accuracy':
      return <AccuracyPanel board={board} scope="plan" />

    case 'models':
      return <ModelsPanel />

    case 'design-doc':
      return (
        <ProjectDesignDoc
          board={board}
          onSelect={p.setSelected}
          live={p.liveTasks}
          onGenerate={p.generateDoc}
          generating={p.generatingDoc}
          error={p.docErr}
          elapsed={p.docElapsed}
          active={p.docActive}
        />
      )

    case 'board':
      return (
        <>
          <div className="plan-toolbar">
            <div className="viewtoggle">
              <button className={p.view === 'kitchen' ? 'on' : ''} onClick={() => p.setView('kitchen')}>
                Explore
              </button>
              <button className={p.view === 'gantt' ? 'on' : ''} onClick={() => p.setView('gantt')}>
                Gantt
              </button>
              <button className={p.view === 'graph' ? 'on' : ''} onClick={() => p.setView('graph')}>
                Graph
              </button>
            </div>
            <Legend />
            <TargetRepoControl value={board.target_repo ?? ''} onSet={p.setTarget} />
          </div>
          <Maximizable title="Plan board">
            {p.view === 'kitchen' ? (
              <OpenKitchenDiagram board={board} selected={p.selected} onSelect={p.setSelected} live={p.liveTasks} />
            ) : p.view === 'gantt' ? (
              <GanttView board={board} selected={p.selected} onSelect={p.setSelected} live={p.liveTasks} />
            ) : (
              <PlanGraph board={board} selected={p.selected} onSelect={p.setSelected} live={p.liveTasks} />
            )}
          </Maximizable>
          {/* The inspector — where "Open in inspector →" goes.
              It had nowhere to go: TaskInspector was exported from PlanningView
              and rendered by NOTHING once that stopped being a page, so
              selecting a task only re-tinted a node behind the drawer that was
              covering it. Every board view feeds the same `selected`, so all
              three get the inspector by being wired here once. */}
          {selectedTask && p.snap ? (
            <TaskInspector
              task={selectedTask}
              snap={p.snap}
              onClose={() => p.setSelected(null)}
              onOp={p.applyOp}
            />
          ) : null}
        </>
      )

    case 'run':
      return (
        <>
          <PlanExecution
            board={board}
            busy={p.busy}
            running={p.running}
            unansweredCritical={(board.questions ?? []).filter((q) => q.critical && !(q.answer ?? '').trim()).length}
            onFinalize={() => p.setPlanStatus('finalized')}
            onReopen={() => p.setPlanStatus('draft')}
            onRunOnce={p.runPlan}
            onStop={p.stopRun}
            onSchedule={p.schedulePlan}
            onUnschedule={p.unschedulePlan}
          />
          {p.runErr ? (
            <ErrorPanel title="Run failed" message={p.runErr} onRetry={() => p.runPlan()} onDismiss={() => p.setRunErr(null)} />
          ) : p.running || p.runReport ? (
            <RunReportPanel
              report={p.runReport}
              running={p.running}
              live={Object.values(p.liveTasks)}
              summary={p.runMsg}
              onSelect={p.setSelected}
              // The fullscreen artifact lives outside App's router state, so
              // "open this run" is announced as an event App turns into a
              // deep-linked tab switch — same capability, no prop threading.
              onOpenRun={(runId) => window.dispatchEvent(new CustomEvent('xnapz:open-run', { detail: runId }))}
              onStep={p.stepRespond}
            />
          ) : null}
        </>
      )
  }
}
